/**
 * VacationRequestList - List view with filtering
 * Story: VAC-DS-03
 */

import React, { useState } from 'react'
import { Calendar, Plus } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'
import type { VacationRequest } from '@/types/vacation'
import { VacationRequestCard } from './VacationRequestCard'
import { EmptyState } from './ui/EmptyState'

interface VacationRequestListProps {
  requests: VacationRequest[]
  isLoading: boolean
  onRequestClick: (request: VacationRequest) => void
  onCancelRequest: (requestId: string) => void
}

const statusLabels: Record<string, string> = {
  ALL: 'Todos',
  PENDING: 'Pendente',
  APPROVED: 'Aprovado',
  REJECTED: 'Rejeitado',
  CANCELLED: 'Cancelado'
}

export const VacationRequestList: React.FC<VacationRequestListProps> = ({
  requests,
  isLoading,
  onRequestClick,
  onCancelRequest
}) => {
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState<string>('ALL')

  const filteredRequests = statusFilter === 'ALL'
    ? requests
    : requests.filter(r => r.status === statusFilter)

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div
            key={i}
            className="card animate-pulse"
            style={{ height: '150px' }}
          >
            <div className="h-6 bg-brand-dark-light rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-brand-dark-light rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-brand-dark-light rounded w-1/2"></div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div>
      {/* Filter Chips */}
      <div className="mb-6 flex gap-2 flex-wrap">
        {['ALL', 'PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'].map(status => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`
              px-4 py-2 rounded-monumenta font-display text-sm transition-all
              ${statusFilter === status
                ? 'bg-brand-primary text-white shadow-monumenta'
                : 'bg-brand-dark-light text-brand-gray hover:text-white hover:bg-brand-dark-light/80'
              }
            `}
          >
            {statusLabels[status] || status}
          </button>
        ))}
      </div>

      {/* Requests List */}
      {filteredRequests.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={Calendar}
            title="Nenhuma solicitação encontrada"
            description={
              statusFilter === 'ALL'
                ? 'Você ainda não fez nenhuma solicitação de férias. Crie sua primeira solicitação agora!'
                : `Nenhuma solicitação com status "${statusLabels[statusFilter]}"`
            }
            action={
              statusFilter === 'ALL' ? (
                <button
                  onClick={() => navigate({ to: '/vacation/request' })}
                  className="btn-primary inline-flex items-center space-x-2"
                >
                  <Plus size={16} />
                  <span>Nova Solicitação</span>
                </button>
              ) : undefined
            }
          />
        </div>
      ) : (
        <div className="space-y-4">
          {filteredRequests.map(request => (
            <VacationRequestCard
              key={request.requestId}
              request={request}
              onView={onRequestClick}
              onCancel={onCancelRequest}
            />
          ))}
        </div>
      )}
    </div>
  )
}
